import { useEffect, useRef, useState } from 'react';
import {
  startChat as startNdaChat,
  sendMessage as sendNdaChatMessage
} from 'app/api/ndaChatApi';
import { pickAssistantText } from './helpers';
import type { FormDataShape } from './types';

interface UseNdaChatArgs {
  formData: FormDataShape;
  selectedLanguage: string;
  selectedTemplateId: string;
  onApplyFields: (fields: Partial<FormDataShape>) => void;
}

export function useNdaChat({
  formData,
  selectedLanguage,
  selectedTemplateId,
  onApplyFields
}: UseNdaChatArgs): {
  messages: Array<{
    id: string;
    type: 'assistant' | 'user';
    text: string;
    time: string;
  }>;
  loading: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  inputPlaceholder: string;
  handleSend: () => Promise<void>;
  handleSyncVariables: (
    override?: Partial<FormDataShape>,
    showResponse?: boolean
  ) => Promise<void>;
} {
  const [messages, setMessages] = useState<
    Array<{
      id: string;
      type: 'assistant' | 'user';
      text: string;
      time: string;
    }>
  >([
    {
      id: 'welcome',
      type: 'assistant',
      text: 'Welcome! I can help you fill the NDA form. Start by typing "create NDA" or, to provide everything in one go, type: "create NDA with counterparty=<name>, address=<address>, email=<email>, project=<project>, effectiveDate=<YYYY-MM-DD>".',
      time: new Date().toLocaleTimeString()
    }
  ]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [chatId, setChatId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [inputPlaceholder, setInputPlaceholder] = useState<string>('');

  const ensureSession = async (): Promise<string | null> => {
    const existing = sessionId ?? chatId;
    if (existing) return existing;
    try {
      const res = await startNdaChat({
        formData,
        language: selectedLanguage,
        templateId: selectedTemplateId ?? undefined
      });
      const newSid = (res as any).sessionId ?? (res as any).chatId ?? null;
      setSessionId(newSid);
      setChatId(((res as any).chatId as string | null) ?? newSid);
      return newSid;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to ensure NDA chat session', e);
      return sessionId ?? chatId ?? null;
    }
  };

  useEffect(() => {
    let active = true;
    const init = async (): Promise<void> => {
      try {
        const res = await startNdaChat({
          formData,
          language: selectedLanguage,
          templateId: selectedTemplateId ?? undefined
        });
        if (!active) return;
        const sid = (res as any).sessionId ?? (res as any).chatId;
        setSessionId(sid ?? null);
        setChatId((res as any).chatId ?? sid ?? null);
        if ((res as any).welcome)
          setMessages((m) => [
            ...m,
            {
              id: `welcome-${Date.now()}`,
              type: 'assistant',
              text: (res as any).welcome!,
              time: new Date().toLocaleTimeString()
            }
          ]);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to start NDA chat session', e);
      }
    };
    if (!sessionId && !chatId) init();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const asString = (v: unknown): string => (typeof v === 'string' ? v : '');

  const processResponseFields = (resp: any): Partial<FormDataShape> => {
    const vars = resp.session?.variables ?? resp.variables ?? {};
    const updatedKeys = resp.updatedKeys;
    const keyMap: Record<string, keyof FormDataShape> = {
      counterpartyName: 'xCompany',
      counterpartyAddress: 'xAddress',
      counterpartyEmail: 'xMail',
      zurichAddress: 'zurichAddress',
      zurichEmail: 'zurichMail',
      projectDescription: 'project',
      effectiveDate: 'date',
      counterpartySigner: 'xSigner',
      zurichSigner: 'zurichSigner'
    };

    const newFields: Partial<FormDataShape> = {};

    if (Array.isArray(updatedKeys) && updatedKeys.length > 0) {
      for (const k of updatedKeys) {
        const formKey = keyMap[k];
        if (formKey) {
          const v = (vars as Record<string, unknown>)[k];
          (newFields as any)[formKey] = asString(v);
        }
      }
    } else {
      const vr = vars as Record<string, unknown>;
      const stringFields = {
        xCompany: asString(vr.counterpartyName),
        xAddress: asString(vr.counterpartyAddress),
        xMail: asString(vr.counterpartyEmail),
        zurichAddress: asString(vr.zurichAddress),
        zurichMail: asString(vr.zurichEmail),
        project: asString(vr.projectDescription),
        date: asString(vr.effectiveDate),
        xSigner: asString(vr.counterpartySigner),
        zurichSigner: asString(vr.zurichSigner)
      };

      for (const [key, value] of Object.entries(stringFields)) {
        if (value?.trim()) {
          (newFields as any)[key] = value;
        }
      }
    }

    return newFields;
  };

  const updatePlaceholderFromResponse = (resp: any): void => {
    const nextExpected = (resp.session?.nextExpected ?? resp.nextExpected) as
      | string
      | undefined;
    if (nextExpected && typeof nextExpected === 'string') {
      setInputPlaceholder(`Enter ${nextExpected}`);
    } else {
      setInputPlaceholder(
        "Ask the assistant or type details (e.g., 'Counterparty is ABC Corp')"
      );
    }
  };

  const handleSend = async (): Promise<void> => {
    const question = inputRef.current?.value?.trim() ?? '';
    if (!question) return;

    setLoading(true);
    setMessages((m) => [
      ...m,
      {
        id: `u-${Date.now()}`,
        type: 'user',
        text: question,
        time: new Date().toLocaleTimeString()
      }
    ]);
    if (inputRef.current) inputRef.current.value = '';

    try {
      const sid = await ensureSession();
      const variablesPayload = buildVariablesPayload(formData);
      const resp = await sendNdaChatMessage({
        sessionId: sid ?? undefined,
        message: question,
        formData,
        metadata: { language: selectedLanguage, variables: variablesPayload }
      });

      const assistantText = pickAssistantText(resp);
      setMessages((m) => [
        ...m,
        {
          id: `a-${Date.now()}`,
          type: 'assistant',
          text: assistantText,
          time: new Date().toLocaleTimeString()
        }
      ]);

      const newFields = processResponseFields(resp);
      if (Object.keys(newFields).length > 0) {
        onApplyFields(newFields);
      }

      updatePlaceholderFromResponse(resp);
      setLoading(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('NDA assistant failed to send message', e);
      setMessages((m) => [
        ...m,
        {
          id: `err-${Date.now()}`,
          type: 'assistant',
          text: 'Sorry, I had trouble answering that. Please try again.',
          time: new Date().toLocaleTimeString()
        }
      ]);
      setLoading(false);
    }
  };

  const buildVariablesPayload = (
    form: FormDataShape
  ): Record<string, unknown> => ({
    counterpartyName: form.xCompany ?? '',
    counterpartyAddress: form.xAddress ?? '',
    counterpartyEmail: form.xMail ?? '',
    zurichAddress: form.zurichAddress ?? '',
    zurichEmail: form.zurichMail ?? '',
    projectDescription: form.project ?? '',
    effectiveDate: form.date ?? '',
    counterpartySigner: form.xSigner ?? '',
    zurichSigner: form.zurichSigner ?? ''
  });

  const computeNewFieldsFromResponse = (
    resp: any,
    override?: Partial<FormDataShape>
  ): Partial<FormDataShape> => {
    const vars = resp?.session?.variables ?? resp?.variables ?? {};
    const updatedKeys = resp?.updatedKeys;

    const keyMap: Record<string, keyof FormDataShape> = {
      counterpartyName: 'xCompany',
      counterpartyAddress: 'xAddress',
      counterpartyEmail: 'xMail',
      zurichAddress: 'zurichAddress',
      zurichEmail: 'zurichMail',
      projectDescription: 'project',
      effectiveDate: 'date',
      counterpartySigner: 'xSigner',
      zurichSigner: 'zurichSigner'
    };

    if (Array.isArray(updatedKeys) && updatedKeys.length > 0) {
      const partial: Partial<FormDataShape> = {};
      for (const k of updatedKeys) {
        const formKey = keyMap[k];
        if (formKey) {
          const v = (vars as Record<string, unknown>)[k];
          (partial as any)[formKey] = asString(v);
        }
      }
      return partial;
    }

    const vr = vars as Record<string, unknown>;
    const candidate: Partial<FormDataShape> = {
      xCompany: asString(vr.counterpartyName),
      xAddress: asString(vr.counterpartyAddress),
      xMail: asString(vr.counterpartyEmail),
      zurichAddress: asString(vr.zurichAddress),
      zurichMail: asString(vr.zurichEmail),
      project: asString(vr.projectDescription),
      date: asString(vr.effectiveDate),
      xSigner: asString(vr.counterpartySigner),
      zurichSigner: asString(vr.zurichSigner)
    };
    const result: Partial<FormDataShape> = {};
    for (const [key, value] of Object.entries(candidate)) {
      const k = key as keyof FormDataShape;
      if (override && Object.hasOwn(override, k)) continue;

      if (value && (value as string).trim()) {
        (result as any)[k] = value as any;
      }
    }
    return result;
  };

  useEffect(() => {
    const applyLanguage = async (): Promise<void> => {
      if (!sessionId && !chatId) return;
      try {
        await sendNdaChatMessage({
          sessionId: sessionId ?? chatId ?? undefined,
          message: '',
          formData,
          metadata: { language: selectedLanguage }
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Failed to apply language change to chat session', e);
      }
    };
    applyLanguage();
  }, [selectedLanguage]);

  const displaySyncResponse = (resp: any): void => {
    const assistantText = pickAssistantText(resp);
    if (assistantText?.trim()) {
      setMessages((m) => [
        ...m,
        {
          id: `sync-${Date.now()}`,
          type: 'assistant',
          text: assistantText,
          time: new Date().toLocaleTimeString()
        }
      ]);
    }
  };

  const applyFilteredFields = (
    newFields: Partial<FormDataShape>,
    override?: Partial<FormDataShape>
  ): void => {
    const filteredFields: Partial<FormDataShape> = {};
    for (const [key, value] of Object.entries(newFields)) {
      if (!override || !Object.hasOwn(override, key)) {
        (filteredFields as any)[key] = value;
      }
    }
    if (Object.keys(filteredFields).length > 0) {
      onApplyFields(filteredFields);
    }
  };

  const handleSyncVariables = async (
    override?: Partial<FormDataShape>,
    showResponse = false
  ): Promise<void> => {
    try {
      const sid = await ensureSession();
      const merged: FormDataShape = override
        ? { ...formData, ...override }
        : formData;
      const variablesPayload = buildVariablesPayload(merged);
      const resp = await sendNdaChatMessage({
        sessionId: sid ?? undefined,
        message: '',
        formData: merged,
        metadata: { language: selectedLanguage, variables: variablesPayload }
      });

      if (showResponse && resp) {
        displaySyncResponse(resp);
      }

      const newFields = computeNewFieldsFromResponse(resp, override);
      if (Object.keys(newFields).length > 0) {
        applyFilteredFields(newFields, override);
      }

      updatePlaceholderFromResponse(resp);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to sync variables', e);
    }
  };

  return {
    messages,
    loading,
    inputRef,
    inputPlaceholder,
    handleSend,
    handleSyncVariables
  };
}
